import {v4 as uuidv4} from 'uuid';
import {ENDPOINT, HTTP_API_ENDPOINT, HTTP_ENDPOINT} from "@/services/endpoints";
import {registerSingleton} from "@/services/GlobalContext";
import ScheduledTask from "@/services/ScheduledTask";
import LinkedSet from "@/services/LinkedSet";

const LOG = localStorage.getItem("log:AsyncApi") ?? false;

class AsyncApi {
    constructor() {
        console.log("Creating async API")

        this.proto = HTTP_ENDPOINT.startsWith("https") ? "wss" : "ws";
        this.endpoint = ENDPOINT;
        this.socket = null;
        this.tabId = null;
        this.isConnected = false;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.reconnectTimeout = 2000;
        this.messageHandlers = new Map();
        this.keepAliveInterval = null;
        if (!this.tabId) {
            this.tabId = sessionStorage.getItem('tab_id') || uuidv4();
            sessionStorage.setItem('tab_id', this.tabId);
        }
        this._initialized = false;
        this._pendingsPosts = [];
        this._connect().then(() => {
            this._initialized = true;
            for(const proc of this._pendingsPosts){
                try {
                    proc();
                } catch (e){
                    console.error(e);
                }
            }
        });

        this._processedMessages = new LinkedSet();
        this._pendingTasks = new Map();

        this._taskWatch = new Map();
        this.setupWatch();
    }

    setupWatch(){
        const setup = (event) => {
            console.log("Inspecting watches")
            const elements = document.querySelectorAll('[data-watch]');
            elements.forEach((element) => {
                const taskId = element.dataset.watch;
                const watchReplacement = element.dataset.watchReplacement;
                const format = element.dataset.watchFormat;

                this.watch(taskId, (data) => {
                    console.log("lol");
                    console.log(data);
                    if(format === "text"){
                        element.textContent = data[watchReplacement];
                    } else {
                        element.htmlContent = data[watchReplacement];
                    }
                });
            });
        };

        if (document.readyState !== 'loading') {
            setup();
        } else {
            document.addEventListener('DOMContentLoaded', setup);
        }
    }

    watch(taskId, onResult, onProgress=null){
        console.log(`Registering watch: ${taskId}`);

        this._taskWatch.set(taskId, {
            onProgress, onResult
        });

        const execFetch = () => {
            setTimeout(() => {
                let path = `${HTTP_ENDPOINT}/async-jobs/listen/${this.tabId}/${taskId}`
                let csrf = {};
                if (window['__csrf__']) {
                    csrf['x-csrf'] = window['__csrf__'];
                }

                fetch(path, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...csrf
                    },
                    body: {}
                }).then(response => {
                    let code = response.status;
                    response.json().then(json => {
                        if (code < 200 || code >= 300) {
                            console.error('Error:', json);
                        } else {
                            console.log(`Watching ${taskId}`);
                        }
                    }).catch(error => {
                        console.error('Error:', error);
                    });
                }).catch(error => {
                    console.error('Error:', error);
                });
            });
        }

        if(!this._initialized){
            this._pendingsPosts.push(execFetch);
        } else {
            execFetch();
        }
    }
    /**
     *
     * @param postPath
     * @param taskInput
     * @return {ScheduledTask}
     */
    scheduleTask(postPath, taskInput) {
        let task = new ScheduledTask(taskInput, this);
        this._pendingTasks.set(task.clientId, task);

        const execFetch = () => {
            setTimeout(() => {
                let path;
                if(postPath.startsWith("/")) {
                    path = `${HTTP_API_ENDPOINT}${postPath}`
                } else {
                    path =`${HTTP_API_ENDPOINT}/${postPath}`
                }
                let csrf = {};
                if(window['__csrf__']){
                    csrf['x-csrf'] = window['__csrf__'];
                }
                console.log("CSRF");

                fetch(path, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...csrf
                    },
                    body: JSON.stringify({
                        tab_id: this.tabId,
                        client_task_id: task.clientId,
                        task_input: taskInput,
                    })
                })
                    .then(response => {
                        let code = response.status;
                        response.json().then(json => {
                            if(code <200 || code >=300){
                                console.error('Error:', json);
                                this._processError({
                                    client_task_id: task.clientId,
                                    error_result: {
                                        error_code: "POST failed",
                                        error_details: json
                                    }
                                });
                            } else {
                                task._serverId = json['server_task_id'];
                                if (task._cancelled) {
                                    this.requestCancellation(task, true);
                                }
                            }
                        }).catch(error => {
                            console.error('Error:', error);
                            this._processError({
                                client_task_id: task.clientId,
                                error_result: {
                                    error_code: "POST failed",
                                    error_details: error
                                }
                            });
                        });
                    })
                    .catch(error => {
                        console.error('Error:', error);
                        this._processError({
                            client_task_id: task.clientId,
                            error_result: {
                                error_code: "POST failed",
                                error_details: error
                            }
                        });
                    });
                task._tsPost = Date.now();
            });
        }

        if(!this._initialized){
            this._pendingsPosts.push(execFetch);
        } else {
            execFetch();
        }
        return task;
    };


    async _connect() {
        return new Promise((resolve, reject) => {
            let resolved = false;
            try {
                this.socket = new WebSocket(`${this.proto}://${this.endpoint}/ws/async-jobs/connect/${this.tabId}`);
                // todo timeout

                this.socket.onopen = () => {
                    this.isConnected = true;
                    this.reconnectAttempts = 0;
                    this._startKeepAlive();
                };

                this.socket.onclose = () => {
                    this.isConnected = false;
                    this._handleDisconnect();
                    if (!resolved) {
                        reject();
                        resolved = true;
                    }
                };

                this.socket.onerror = (error) => {
                    console.error('WebSocket error:', error);
                    if (!resolved) {
                        reject();
                        resolved = true;
                    }
                };

                this.socket.onmessage = (event) => {
                    LOG && console.log(event);
                    if(event.data === "registered"){
                        console.log('WebSocket connected');
                        resolve(true);
                        resolved = true;

                        return;
                    }
                    const data = JSON.parse(event.data);
                    this._handleMessage(data);
                };
            } catch (error) {
                console.error('WebSocket connection error:', error);
                this._handleDisconnect();
                if (!resolved) {
                    reject();
                    resolved = true;
                }
            }
        });
    }


    _handleDisconnect() {
        this._stopKeepAlive();
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++;
        }
        setTimeout(() => this._connect(), this.reconnectTimeout * this.reconnectAttempts);
    }

    _startKeepAlive() {
        this.keepAliveInterval = setInterval(() => {
            if (this.isConnected) {
                this.socket.send('ping');
            }
        }, 30000); // Send keep-alive every 30 seconds
    }

    _stopKeepAlive() {
        if (this.keepAliveInterval) {
            clearInterval(this.keepAliveInterval);
            this.keepAliveInterval = null;
        }
    }


    _handleMessage(data) {
        if(data['message_id']){
            if(this._processedMessages.has(data['message_id'])){
                console.log(`Duplicated message: ${data['message_id']}, ignoring`);
                return;
            }
            this._processedMessages.add(data['message_id']);
        }
        if(data.type === "task_progress" || data.type == "task_result"){
            return this._handlePushEvent(data);
        }

        const clientId = data['client_task_id'];

        if(!this._pendingTasks.has(clientId)){
            if(data.type === "error" && "TaskCancelledException" === data['error_result']['error_code']){
                return;
            }
            console.log(`Task not found: ${clientId}`);
            return;
        }

        if(data.type === "result"){
            this._processResult(data);
            return;
        }
        if(data.type === "progress"){
            this._processProgress(data);
            return;
        }
        if(data.type === "error"){
            this._processError(data);
            return;
        }

        console.log(`Unknown message type: ${data.type}`);
    }

    _disconnect() {
        if (this.socket) {
            this._stopKeepAlive();
            this.socket.close();
            this.socket = null;
            this.isConnected = false;
        }
    }

    _processProgress(data){
        console.log("Processing progress:", data)
        const clientId = data['client_task_id'];
        const task = this._pendingTasks.get(clientId);
        task._lastUpdate = Date.now();

        try {
            task._progressResolve?.(data['progress']);
        } catch (e){
            console.error(e);
        }
    }

    _processResult(data){
        const clientId = data['client_task_id'];

        /**
         * @type {ScheduledTask}
         */
        const task = this._pendingTasks.get(clientId);
        if(task._cancelled){
            return;
        }

        this._pendingTasks.delete(clientId);

        task._tsFinished = task._lastUpdate = Date.now();

        try {
            task._progressResolve(null);
        } catch (e){
            console.error(e);
        }
        try {
            task._endedWithSuccess = true;
            task.result = data.result['task_output'];
            task.warnings = data.result['warnings'];
            task.metadata = data.result['task_metadata'];

            task._endResolve?.(task.result);
        } catch (e){
            console.error(e);
        }
    }

    _processError(data){
        const clientId = data['client_task_id'];

        /**
         * @type {ScheduledTask}
         */
        const task = this._pendingTasks.get(clientId);
        this._pendingTasks.delete(clientId);

        task._tsFinished = task._lastUpdate = Date.now();

        try {
            task._progressResolve(null);
        } catch (e){
            console.error(e);
        }
        try {
            task._endedWithSuccess = false;

            if("TaskCancelledException" === data['error_result']['error_code']){
                task._cancelled = true;
                task._endResolve?.(null);
                return;
            }

            task._endedWithError = true;
            task.error = data['error_result'];
            task._endReject?.(task.error);
        } catch (e){
            console.error(e);
        }
    }

    requestCancellation = function (scheduledTask, onlyNotifyServer=false) {
        const clientId = scheduledTask.clientId;
        if(scheduledTask._serverId) {
            fetch(`${HTTP_ENDPOINT}/async-jobs/cancel/${scheduledTask._serverId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({})
            })
                .then(() => LOG && console.log(`Task ${clientId} cancelled`))
                .catch(error => console.error('Error:', error));
        }

        if(!onlyNotifyServer) {
            scheduledTask._cancelled = true;
            this._pendingTasks.delete(clientId);

            scheduledTask._tsFinished = scheduledTask._lastUpdate = Date.now();
            try {
                scheduledTask._progressResolve?.(null);
            } catch (e){
                console.error(e);
            }

            try {
                scheduledTask._endResolve?.(null);
            } catch(e){
                console.error(e);
            }
        }
    };

    _handlePushEvent(data) {
        console.log(data);
        if(data.type === "task_progress"){
            if(this._taskWatch.has(data['event_uuid'])){
                console.log(data.progress);
                this._taskWatch.get(data['event_uuid']).onProgress?.(data.progress);
            }
            return;
        }
        if(data.type === "task_result"){
            console.log(`Received event: ${data['event_uuid']}`);
            if(this._taskWatch.has(data['event_uuid'])){
                this._taskWatch.get(data['event_uuid']).onResult?.(data.result);
            }
        }
    }
}

/**
 * @type {AsyncApi}
 */
function asyncApiFactory(){
    // if (!window['__csrf__']) {
    //     console.warn("Static page, AsynApi not available");
    //     return;
    // }

    return registerSingleton("asyncApi",
        () => new AsyncApi());
}

export function setupWatches(){
    if(document.querySelector('[data-watch]')){
        if (!window['__csrf__']) {
            console.warn("Static page, AsyncApi not available");
            return;
        }

        registerSingleton("asyncApi",
            () => new AsyncApi());
    }
}

export default asyncApiFactory
